import React from 'react';
import { useSelector } from 'react-redux';
import { Loading, SystemText } from 'e4shared';
import Filters from '../_filters';
import Chart from './chart';
import GroupPicker from 'components/admin/forms/groupPicker';
import PeriodPicker from 'components/admin/forms/periodPicker';
import WidgetBase, { groupSet, widgetLoad, widgetLoadRecommendations } from '../_base';
import CompetencyRecommendations from '../components/competencyRecommendations';

export const RampTimes = ({ defaultProps, id, item, setItem, onDelete }) => {
    const showFilters = useSelector(state => state.ui.filterWidget);
    const user = useSelector(state => state.user.user);
    const [values, setValues] = React.useState({});
    const [errors, setErrors] = React.useState({});
    const [notConfigured, setNotConfigured] = React.useState(false);
    const [initialised, setInitialised] = React.useState(false);
    const [loading, setLoading] = React.useState(true);
    const [recLoading, setRecLoading] = React.useState(true);
    const [data, setData] = React.useState([]);
    const [recommendationData, setRecommendationData] = React.useState({});
    const [keys, setKeys] = React.useState([]);

    const text = {
        fields: {
            group: {
                label: SystemText('Dashboard/Widgets/RampTimes/Fields/Group/Label'),
                help: SystemText('Dashboard/Widgets/RampTimes/Fields/Group/Help'),
                error: SystemText('Dashboard/Widgets/RampTimes/Fields/Group/Error'),
            },
            period: {
                help: SystemText('Dashboard/Widgets/RampTimes/Fields/Period/Help'),
                error: SystemText('Dashboard/Widgets/RampTimes/Fields/Period/Error'),
            },
        },
    };

    React.useEffect(() => {
        if (item) {
            const parsed = JSON.parse(item.data);
            setValues({ ...defaults, ...parsed });
            setInitialised(true);
        }
    }, [item]);

    React.useEffect(() => {
        initialised && load();
    }, [initialised]);

    const load = () => {
        const override = response => {
            setData(
                response.data.map(r => {
                    const set = { id: r.name };
                    r.data.forEach(d => {
                        set[d.x] = d.y;
                    });
                    return set;
                })
            );
            setRecommendationData(response.recommendation);
            setKeys(response.data[0].data.map(d => d.x));
        };

        widgetLoad(
            item.id,
            user.guid,
            validator,
            setLoading,
            setNotConfigured,
            setData,
            () => {
                widgetLoadRecommendations(item.id, user.guid, setRecLoading, setRecommendationData);
            },
            override
        );
    };

    const updateProperty = (value, prop) => {
        const temp = { ...values };
        temp[prop] = value;
        setValues(temp);
    };

    const height = defaultProps.height(item);

    const defaults = { show_For: true, period: 'M1' };

    const validator = suppressErrors => {
        const form = values || JSON.parse(item.data);
        const errors = {};
        let validForm = true;

        if (!form.name) {
            errors.name = SystemText('Dashboard/Widgets/General/Edit/Fields/Name/Error');
            validForm = false;
        }

        if (!groupSet(form.group1)) {
            errors.group1 = text.fields.group.error;
            validForm = false;
        }

        if (!form.period) {
            errors.period = text.fields.period.error;
            validForm = false;
        }

        if (!suppressErrors) setErrors(errors);
        return validForm;
    };

    const chart = (
        <div className="chart-container" id={`chart-${item.id}`} style={{ height: `${height}px` }}>
            <Chart data={data} item={item} id={`chart-${item.id}`} keys={keys} />
        </div>
    );

    const recommendations = recLoading ? (
        <Loading />
    ) : recommendationData?.comps ? (
        <CompetencyRecommendations
            text={SystemText('Dashboard/Widgets/RampTimes/Recommends/Strongest', recommendationData.group)}
            comps={recommendationData?.comps}
        />
    ) : null;

    const filters = (
        <Filters
            validator={validator}
            widget={item}
            values={values}
            errors={errors}
            onChange={(value, prop) => updateProperty(value, prop)}
            refresh={x => {
                setItem(x);
                load();
            }}
            show={showFilters === id}
            onDelete={onDelete}
        >
            <GroupPicker
                label={text.fields.group.label}
                mandatory
                allowMultiple={false}
                value={values.group1}
                error={errors.group1}
                onChange={value => updateProperty(value, 'group1')}
                help={text.fields.group.help}
                showUserOption={defaultProps.company}
                controlId={id}
            />
            <PeriodPicker
                value={values.period}
                error={errors.period}
                onChange={value => updateProperty(value, 'period')}
                help={text.fields.period.help}
            />
        </Filters>
    );

    return (
        <WidgetBase
            filters={filters}
            chart={chart}
            height={height}
            item={item}
            recommendations={recommendations}
            id={id}
            help={null}
            hideConfig={defaultProps.company && !user.administrator}
            extra={null}
            loading={loading}
            notConfigured={notConfigured}
        />
    );
};

export default RampTimes;
